import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, FileText, Handshake, MessageCircle, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function ProgressiveDisclosureInfo() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar />
      
      <main className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Main Headline & Introduction */}
        <section className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary font-medium">
            <Shield className="w-4 h-4" />
            <span>Privacy-First Approach</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
            You're in Control of Your Story
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            On our platform, we use a special feature called Progressive Disclosure to put you in the driver's seat. 
            It's all about protecting your privacy and making sure you're judged on your skills first.
          </p>
        </section>

        {/* So, What Is It, Then? */}
        <Card className="p-8 mb-12 bg-white shadow-lg">
          <h2 className="text-3xl font-bold mb-6 text-foreground">
            So, What Is Progressive Disclosure, Then?
          </h2>
          
          <div className="space-y-4 text-lg text-muted-foreground">
            <p>
              Think of it like getting to know someone. You wouldn't share your entire life story the moment you meet. 
              You share things gradually as you build trust. Progressive Disclosure works the same way for your job application.
            </p>
            
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg">
              <p className="font-semibold text-foreground">
                Here's the important bit: You do not have to put your criminal record details on your initial CV or application.
              </p>
            </div>
          </div>
        </Card>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">
            How It Works: A Simple Step-by-Step
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <Card className="p-6 text-center hover:shadow-xl transition-shadow bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <div className="w-8 h-8 mx-auto mb-4 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">You Apply</h3>
              <p className="text-muted-foreground">
                You find a job you like and apply with your CV. The employer sees your skills, your experience, 
                and your potential – and nothing else.
              </p>
            </Card>

            {/* Step 2 */}
            <Card className="p-6 text-center hover:shadow-xl transition-shadow bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/10 rounded-full flex items-center justify-center">
                <Handshake className="w-8 h-8 text-amber-500" />
              </div>
              <div className="w-8 h-8 mx-auto mb-4 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">The Employer is Interested</h3>
              <p className="text-muted-foreground">
                The employer is impressed and decides they want to take things further. This is what we call 'mutual interest'.
              </p>
            </Card>

            {/* Step 3 */}
            <Card className="p-6 text-center hover:shadow-xl transition-shadow bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/10 rounded-full flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-green-500" />
              </div>
              <div className="w-8 h-8 mx-auto mb-4 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">You Share on Your Terms</h3>
              <p className="text-muted-foreground">
                Only after the employer has shown genuine interest in you as a candidate is any information about your past shared. 
                This means when the conversation happens, you're already in a strong position, valued for what you can do.
              </p>
            </Card>
          </div>
        </section>

        {/* The Main Benefit */}
        <Card className="p-8 mb-12 bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-primary/20 shadow-lg">
          <h2 className="text-3xl font-bold mb-6 text-foreground text-center">
            Why This Puts You at an Advantage
          </h2>
          
          <div className="bg-white p-6 rounded-lg mb-6 shadow-md">
            <p className="text-xl font-semibold text-primary text-center italic">
              "Share your story on your terms. Criminal record information is only shared when there's mutual interest, 
              protecting your privacy."
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1">Skills First</h3>
                <p className="text-muted-foreground">You are judged on your skills, not your past.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1">Fair Chance</h3>
                <p className="text-muted-foreground">It stops the automatic 'no' before you get a chance.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1">Build Confidence</h3>
                <p className="text-muted-foreground">It gives you the confidence to apply for roles you're qualified for.</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Visual Infographic */}
        <section className="mb-12">
          <Card className="p-8 bg-white shadow-lg">
            <h3 className="text-2xl font-bold mb-8 text-center text-foreground">
              The Progressive Disclosure Journey
            </h3>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              {/* CV to Employer */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <FileText className="w-10 h-10 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground text-center">Your CV</p>
              </div>

              <ArrowRight className="w-8 h-8 text-muted-foreground rotate-90 md:rotate-0" />

              {/* Mutual Interest */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mb-3">
                  <Handshake className="w-10 h-10 text-amber-500" />
                </div>
                <p className="text-sm text-muted-foreground text-center">Mutual Interest</p>
              </div>

              <ArrowRight className="w-8 h-8 text-muted-foreground rotate-90 md:rotate-0" />

              {/* Controlled Conversation */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-3">
                  <MessageCircle className="w-10 h-10 text-green-500" />
                </div>
                <p className="text-sm text-muted-foreground text-center">Your Disclosure</p>
              </div>
            </div>
          </Card>
        </section>

        {/* Call to Action */}
        <section className="text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Ready to Let Your Skills Do the Talking?
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start your journey today with a platform that values your potential, not your past.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/jobseeker-auth">
                Get Started Now
                <ArrowRight className="ml-2" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="text-lg px-8">
              <Link to="/progressive-disclosure">
                Manage Your Privacy Settings
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
